import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import ProductsOfferings from '../components/home/ProductsOfferings';
import SpineStory from '../components/home/SpineStory';
import ProofDemos from '../components/home/ProofDemos';
import { btnGhost, btnPrimary } from '../components/ui';
import { faqSchema, organizationSchema, professionalServiceSchema } from '../seo/schema';

interface Step {
  title: string;
  body: string;
}

interface Card {
  title: string;
  body: string;
}

interface FaqItem {
  q: string;
  a: string;
}

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const steps = t('how.steps', { returnObjects: true }) as Step[];
  const who = t('who.cards', { returnObjects: true }) as Card[];
  const faqs = t('faq.items', { returnObjects: true }) as FaqItem[];
  const motionProps = reduce
    ? {}
    : { initial: 'hidden' as const, whileInView: 'show' as const, viewport: { once: true, margin: '-40px' }, variants: reveal };

  return (
    <>
      <SEO jsonLd={[organizationSchema, professionalServiceSchema, faqSchema]} />
      <section className="relative overflow-hidden" aria-labelledby="hero-title">
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-70" aria-hidden="true" />
        <svg className="pointer-events-none absolute inset-y-0 right-0 hidden w-[46%] lg:block" viewBox="0 0 560 520" aria-hidden="true">
          <path d="M8 430 C 120 250, 180 390, 280 230 S 430 70, 520 110" fill="none" stroke="rgba(232,163,23,0.4)" strokeWidth="2" />
          <circle cx="150" cy="330" r="5" fill="#e8a317" />
          <circle cx="300" cy="210" r="5" fill="#3dffc5" />
          <circle cx="500" cy="108" r="6" fill="#e8a317" />
          <text x="150" y="316" fill="#9a958a" fontSize="13" textAnchor="middle">{t('hero.nodePlan')}</text>
          <text x="300" y="196" fill="#9a958a" fontSize="13" textAnchor="middle">{t('hero.nodeExecute')}</text>
          <text x="500" y="94" fill="#9a958a" fontSize="13" textAnchor="middle">{t('hero.nodeSettle')}</text>
        </svg>
        <div className="relative mx-auto grid max-w-[1220px] items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:py-24">
          <div>
            <p className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono normal-case tracking-normal text-amber">01</span>
              {t('hero.eyebrow')}
            </p>
            <motion.h1
              id="hero-title"
              className="font-display text-[2.6rem] font-medium leading-[1.05] tracking-tight text-paper sm:text-6xl"
              {...(reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } })}
            >
              {t('hero.line1')}
              <br />
              <span className="bg-gradient-to-r from-amber via-[#f0c45a] to-mint bg-clip-text text-transparent">
                {t('hero.line2')}
              </span>
              <br />
              {t('hero.line3')} <em className="italic text-mint">{t('hero.line4')}</em>
            </motion.h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted sm:text-lg">
              {t('hero.ledeBefore')} <strong className="font-semibold text-paper">{t('hero.ledeStrong')}</strong>
              {t('hero.ledeAfter')}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link className={btnPrimary} to="/#talk">{t('hero.ctaPrimary')}</Link>
              <Link className={btnGhost} to="/products">{t('hero.ctaSecondary')}</Link>
            </div>
            <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted" aria-label={t('hero.proofAria')}>
              <li><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-mint" />{t('hero.proof1')}</li>
              <li><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber" />{t('hero.proof2')}</li>
              <li><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-coral" />{t('hero.proof3')}</li>
            </ul>
          </div>
          <aside className="rounded-2xl border border-line bg-graphite/90 p-5 shadow-card backdrop-blur" aria-label={t('hero.cardAria')}>
            <header className="mb-4 flex items-center justify-between gap-3">
              <p className="font-mono text-[0.68rem] uppercase tracking-[0.08em] text-stamp">{t('hero.cardKicker')}</p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-mint/30 px-2 py-0.5 font-mono text-[0.68rem] text-mint">
                <i className="h-1.5 w-1.5 rounded-full bg-mint shadow-[0_0_6px_#3dffc5]" />
                {t('hero.cardPill')}
              </span>
            </header>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
              <div>
                <h2 className="mb-2 text-xs uppercase tracking-[0.12em] text-muted">{t('hero.before')}</h2>
                <ul className="space-y-2">
                  {[1, 2, 3, 4].map((n) => (
                    <li key={n} className="rounded-lg border border-coral/30 bg-ink px-2 py-2 text-[0.82rem] text-[#ffb3c0]">
                      {t(`hero.before${n}`)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="self-center text-xl font-semibold text-amber" aria-hidden="true">→</div>
              <div>
                <h2 className="mb-2 text-xs uppercase tracking-[0.12em] text-muted">{t('hero.after')}</h2>
                <ul className="space-y-2">
                  {[1, 2, 3, 4].map((n) => (
                    <li key={n} className="rounded-lg border border-mint/30 bg-ink px-2 py-2 text-[0.82rem] text-[#b8ffe8]">
                      {t(`hero.after${n}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-4 font-mono text-[0.72rem] text-muted">{t('hero.cardFoot')}</p>
          </aside>
        </div>
      </section>

      <section id="how" className="scroll-mt-20 px-4 py-20 sm:px-6" aria-labelledby="how-title">
        <div className="mx-auto max-w-[1180px]">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">02</span>
              {t('how.eyebrow')}
            </p>
            <h2 id="how-title" className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{t('how.title')}</h2>
            <p className="mt-4 text-lg text-muted">{t('how.lede')}</p>
          </div>
          <ol className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {Array.isArray(steps) && steps.map((step, index) => (
              <motion.li key={step.title} className="rounded-2xl border border-line bg-graphite p-4" {...motionProps} transition={{ delay: index * 0.05 }}>
                <span className="font-mono text-sm text-amber">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="mt-3 font-display text-xl">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{step.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <ProductsOfferings />

      <section id="automate" className="px-4 py-16 sm:px-6" aria-labelledby="automate-title">
        <div className="mx-auto max-w-[1180px]">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">04</span>
              {t('automate.eyebrow')}
            </p>
            <h2 id="automate-title" className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{t('automate.title')}</h2>
            <p className="mt-4 text-lg text-muted">{t('automate.lede')}</p>
          </div>
          <SpineStory />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <article className="rounded-2xl border border-line bg-graphite p-5">
              <h3 className="font-display text-2xl">{t('domain.dist.title')}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted">{t('domain.dist.body')}</p>
              <p className="mt-3 font-mono text-xs text-stamp">{t('domain.dist.note')}</p>
            </article>
            <article className="rounded-2xl border border-line bg-graphite p-5">
              <h3 className="font-display text-2xl">{t('domain.factory.title')}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted">{t('domain.factory.body')}</p>
              <p className="mt-3 font-mono text-xs text-stamp">{t('domain.factory.note')}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="proof" className="px-4 py-16 sm:px-6" aria-labelledby="proof-title">
        <div className="mx-auto max-w-[1180px]">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">05</span>
              {t('proof.eyebrow')}
            </p>
            <h2 id="proof-title" className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{t('proof.title')}</h2>
            <p className="mt-4 text-lg text-muted">
              {t('proof.ledeBefore')} <strong className="text-paper">{t('proof.ledeStrong')}</strong> {t('proof.ledeAfter')}
            </p>
          </div>
          <ProofDemos />
        </div>
      </section>

      <section id="who" className="px-4 py-16 sm:px-6" aria-labelledby="who-title">
        <div className="mx-auto max-w-[1180px]">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">06</span>
              {t('who.eyebrow')}
            </p>
            <h2 id="who-title" className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{t('who.title')}</h2>
            <p className="mt-4 text-lg text-muted">{t('who.lede')}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {Array.isArray(who) && who.map((card) => (
              <article key={card.title} className="rounded-2xl border border-line bg-graphite p-5">
                <h3 className="font-display text-2xl">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted">{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="scroll-mt-20 px-4 py-16 sm:px-6" aria-labelledby="faq-title">
        <div className="mx-auto max-w-3xl">
          <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
            <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">07</span>
            {t('faq.eyebrow')}
          </p>
          <h2 id="faq-title" className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{t('faq.title')}</h2>
          <div className="mt-8 space-y-3">
            {Array.isArray(faqs) && faqs.map((item, index) => (
              <details key={item.q} className="group rounded-2xl border border-line bg-graphite px-4 py-3" open={index === 0}>
                <summary className="cursor-pointer list-none font-medium text-paper marker:content-none [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center justify-between gap-4">
                    {item.q}
                    <span className="font-mono text-amber group-open:hidden">+</span>
                    <span className="hidden font-mono text-amber group-open:inline">–</span>
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted">
                  <Trans
                    i18nKey={`faq.items.${index}.a`}
                    components={{
                      strong: <strong className="font-semibold text-paper" />,
                      a: <a className="text-amber hover:text-mint" href="mailto:info@simpian.com">info@simpian.com</a>,
                    }}
                  />
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="talk" className="scroll-mt-20 px-4 py-16 sm:px-6" aria-labelledby="cta-title">
        <div className="mx-auto grid max-w-[1180px] gap-8 rounded-[28px] border border-line bg-graphite p-5 sm:p-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
              <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">08</span>
              {t('cta.eyebrow')}
            </p>
            <h2 id="cta-title" className="font-display text-4xl font-medium tracking-tight">{t('cta.title')}</h2>
            <p className="mt-4 text-muted">{t('cta.lede')}</p>
            <address className="mt-8 text-sm not-italic text-muted">
              <span className="block text-paper">{t('cta.addressName')}</span>
              {t('cta.addressLine')}
              <span className="mt-3 inline-flex rounded-full border border-stamp/40 px-2 py-1 font-mono text-[0.68rem] text-stamp">
                {t('cta.geo')}
              </span>
            </address>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default HomePage;
